import { TrackEvent } from '@enums';
import { Enum_Modals_Trigger } from '@graphql/generated/graphql';
import { useAnalytics } from '@lib/analytics/analyticsContext';
import { AnimationControls } from 'framer-motion';
import { useRouter } from 'next/router';
import {
    createContext,
    Dispatch,
    FC,
    PropsWithChildren,
    useCallback,
    useContext,
    useEffect,
    useReducer,
} from 'react';
import { useCounter } from 'react-use';
import { getValidPageIndex } from '../utils/getValidPageIndex';
import { useNavigationContext } from './NavigationContext';

interface ProgressContext {
    isPaused: boolean;
    numberOfPages: number;
    currentPageIndex: number;
    animationControls?: AnimationControls;
    pageDuration?: number;
    startAnimation?: (duration?: number, isInfinite?: boolean) => void;
    pauseAnimation?: () => void;
    resumeAnimation?: () => void;
    resetAnimation?: () => void;
    loopCountInc: () => void;
}

export interface ProgressContextProps extends PropsWithChildren {
    numberOfPages: number;
    initialPageIndex?: number;
}

type ProgressAction =
    | { type: 'START'; payload?: { duration: number; isInfinite?: boolean } }
    | { type: 'PAUSE' }
    | { type: 'RESUME' }
    | { type: 'RESET' }
    | { type: 'RESTART' }
    | { type: 'PREVIOUS_PAGE' }
    | { type: 'NEXT_PAGE' }
    | {
          type: 'SET_ANIMATIONS';
          payload: {
              startAnimation: (duration?: number, isInfinite?: boolean) => void;
              pauseAnimation: () => void;
              resumeAnimation: () => void;
              resetAnimation: () => void;
          };
      };

const partialInitialContext = {
    isPaused: false,
    numberOfPages: 0,
    currentPageIndex: 0,
    isMuted: false,
    loopCountInc: () => {},
};

const progressReducer = (state: ProgressContext, action: ProgressAction) => {
    switch (action.type) {
        case 'START':
            state.startAnimation?.(
                action.payload?.duration,
                action.payload?.isInfinite
            );
            return {
                ...state,
                isPaused: false,
                pageDuration: action.payload?.duration,
            };

        case 'PAUSE':
            state.pauseAnimation?.();
            return { ...state, isPaused: true };

        case 'RESUME':
            state.resumeAnimation?.();
            return { ...state, isPaused: false };

        case 'RESET': {
            state.resetAnimation?.();
            return state;
        }

        case 'RESTART': {
            state.loopCountInc();
            return state;
        }

        case 'PREVIOUS_PAGE':
            return {
                ...state,
                currentPageIndex: Math.max(state.currentPageIndex - 1, 0),
            };

        case 'NEXT_PAGE':
            return {
                ...state,
                currentPageIndex: Math.min(
                    state.currentPageIndex + 1,
                    state.numberOfPages - 1
                ),
            };

        case 'SET_ANIMATIONS':
            return {
                ...state,
                startAnimation: action.payload.startAnimation,
                pauseAnimation: action.payload.pauseAnimation,
                resumeAnimation: action.payload.resumeAnimation,
                resetAnimation: action.payload.resetAnimation,
            };

        default:
            return state;
    }
};

export const ProgressContext = createContext<ProgressContext>(
    partialInitialContext
);

export const ProgressDispatchContext = createContext<Dispatch<ProgressAction>>(
    () => {}
);

export const ProgressProvider: FC<ProgressContextProps> = ({
    children,
    numberOfPages,
    initialPageIndex,
}) => {
    const { track } = useAnalytics();
    const router = useRouter();
    const { storyPages, story } = useNavigationContext();

    const [, { inc: loopCountInc, reset: loopCountReset, get: loopCountGet }] =
        useCounter(0);

    const initialContext: ProgressContext = {
        ...partialInitialContext,
        numberOfPages,
        currentPageIndex: getValidPageIndex({
            numberOfPages,
            index: initialPageIndex,
        }),
        loopCountInc,
    };
    const [state, dispatch] = useReducer(progressReducer, {
        ...initialContext,
    });

    const handleRouteChange = useCallback(
        (targetUrl: string) => {
            if (
                router.asPath.includes('onboarding') &&
                !targetUrl.includes('onboarding')
            ) {
                track(TrackEvent.ExitOnboarding, {
                    current_step: state.currentPageIndex + 1,
                    total_steps: state.numberOfPages,
                });
            }
        },
        [router.asPath, state.currentPageIndex, state.numberOfPages, track]
    );

    useEffect(() => {
        if (!story) return;

        track(TrackEvent.DisplayStoryPage, {
            event: Enum_Modals_Trigger.DisplayStoryPage,
            title: storyPages[state.currentPageIndex].pageTitle,
            storySlug: story.Slug!,
        });

        if (state.currentPageIndex == state.numberOfPages - 1) {
            track(TrackEvent.LastStoryPage, {
                event: Enum_Modals_Trigger.LastStoryPage,
                storySlug: story.Slug!,
            });
        }

        if (state.currentPageIndex == 0) {
            track(TrackEvent.FirstStoryPage, {
                event: Enum_Modals_Trigger.FirstStoryPage,
                storySlug: story.Slug!,
            });
        }

        const t_init = performance.now();
        const param = {
            story_title: story.Title,
            index: state.currentPageIndex,
            page_title: storyPages[state.currentPageIndex].pageTitle,
        };

        router.events.on('routeChangeStart', handleRouteChange);

        return () => {
            // only send event when user pass
            track(TrackEvent.StoryPageView, {
                ...param,
                video_duration: state.pageDuration || 0,
                view_duration: Math.round(performance.now() - t_init),
                loop_count: loopCountGet(),
            });
            loopCountReset();
            router.events.off('routeChangeStart', handleRouteChange);
        };
    }, [
        router.events,
        state.currentPageIndex,
        state.numberOfPages,
        state.pageDuration,
        story,
        storyPages,
        handleRouteChange,
        loopCountGet,
        loopCountReset,
        track,
    ]);

    return (
        <ProgressContext.Provider value={state}>
            <ProgressDispatchContext.Provider value={dispatch}>
                {children}
            </ProgressDispatchContext.Provider>
        </ProgressContext.Provider>
    );
};

export const useProgressContext = () => useContext(ProgressContext);

export const useProgressDispatchContext = () =>
    useContext(ProgressDispatchContext);
